import { useEffect, useMemo, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { TopMenu } from '../../components/menus/topMenu/TopMenu'
import { MoveZone } from '../../components/zone/moveZone/MoveZone'
import { translations } from '../../configs/translations'
import { RaceTypes } from '../../enums/RaceTypes'
import { UserProfile } from '../../models/UserProfile'
import { VehicleService } from '../../services/VehicleService'
import { useStore } from '../../store/store'
import { Header } from '../main/header/Header'
import { FineTuningCache } from './FineTuning.cache'
import './FineTuning.css'
import { FineTuningComponentData, FineTuningProfile, FineTuningVehicle } from './FineTuning.types'
import { FineTuningCaracteristicsService } from './fineTuningCharacteristics/FineTuningCaracteristics.service'
import { FineTuningCharacteristics } from './fineTuningCharacteristics/FineTuningCharacteristics'
import { FineTuningComponent } from './fineTuningComponent/FineTuningComponent'

export const FineTuning = () => {

	const language = useStore(state => state.language)
	const t = translations[language]

	const [searchParams] = useSearchParams()
	const vehicleId = Number(searchParams.get('vehicleId')) || Number(localStorage.getItem('lastVehicleId'))
	const [profile, setProfile] = useState<string>("race")
	const [fineTuning, setFineTuning] = useState<FineTuningVehicle | null>(null)
	const [activeComponent, setActiveComponent] = useState<string>("")
	const [fineTuningSettingsRef, setFineTuningSettingsRef] = useState<number>(0)
	const [performanceService, setPerformanceService] = useState<FineTuningCaracteristicsService | null>(null)
	const [activeProfile, setActiveProfile] = useState<FineTuningProfile | null>(null)

	const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))

	const topMenuStyles = useMemo(() => ({
		button: {
			active: {
				backgroundColor: profile === 'race' ? '#FD0282' : '#b16a00',
			},
			inactive: {
				backgroundColor: '#fff',
				color: '#341733'
			}
		}
	}), [profile])

	useEffect(() => {

		const initCacheService = async (settings: FineTuningVehicle) => {
			if (vehicleId && !Number.isNaN(Number(vehicleId)) && settings) {
				const cache = FineTuningCache.getInstance()
				await cache.initCache(vehicleId, settings)
			}
		}

		const handleResponse = async (settings: FineTuningVehicle) => {
			setFineTuning(settings)
			const activeProfile = settings.profiles.find(profile => profile.active) ?? settings.profiles[0]
			setProfile(activeProfile.name.toLowerCase())
			await initCacheService(settings)
			setActiveProfile(activeProfile)
			setActiveComponent(activeProfile.components[0].name)
			FineTuningCaracteristicsService.getInstance().setHandling(activeProfile.baseHandling)
			FineTuningCaracteristicsService.getInstance().setBaseParameters(activeProfile.l2Parameters)
			setPerformanceService(FineTuningCaracteristicsService.getInstance())
			VehicleService.purgeGameVehicleHandling(vehicleId)
		}

		if (vehicleId && !Number.isNaN(Number(vehicleId))) {
			VehicleService.getFineTuning(vehicleId)
				.then(handleResponse)
				.catch((error) => {
					toast.error(
						error && error.response && error.response.data && t[error.response.data as keyof typeof t]
							? t[error.response.data as keyof typeof t]
							: t.error_unknown,
						{ className: 'notification-error' }
					)
				})
		}

		return () => {
			localStorage.removeItem("fine-tuning-storage")
		}
	}, [vehicleId])

	const handlePageChange = (component: string) => {
		FineTuningCache.getInstance().updateOpenedCategory(component, profile)
		setActiveComponent(component)
	}

	useEffect(() => {
		const profileData = FineTuningCache.getInstance().getProfileByName(profile, FineTuningCache.getInstance().getCache())
		if (!profileData) return
		setActiveComponent(profileData.openedCategory)
	}, [profile])

	return (
		<div>
			<Header
				profile={userProfile}
				setProfile={setUserProfile}
				selectedMode={RaceTypes.RANKED}
				selectedVehicle={null}
				title={t.fine_tuning}
			/>
			<div className='fine-tuning-top-menu-wrapper' key={profile}>
				{fineTuning && activeComponent && <TopMenu
					key={`${profile}-${activeComponent}-${JSON.stringify(topMenuStyles)}`}
					styles={topMenuStyles}
					pages={fineTuning.profiles[0].components.map((component) => {
						return {
							name: component.name,
							text: t[component.translation as keyof typeof t],
							onClick: () => { handlePageChange(component.name) },
							isActive: activeComponent === component.name
						}
					})}
					initPage={activeComponent}
				/>}
			</div>
			<div className='fine-tuning-component-wrapper'>
				{activeProfile && <FineTuningComponent
					key={profile}
					profile={profile}
					setSelectedProfile={setProfile}
					component={
						activeProfile.components
							.find((component) => component.name === activeComponent) as FineTuningComponentData
					}
					setFineTuningSettingsRef={setFineTuningSettingsRef}
				/>}
			</div>
			<div className='fine-tuning-characteristics-wrapper'>
				{activeProfile &&
					<FineTuningCharacteristics profile={profile}
						performanceCategories={activeProfile.components.find((component) => component.name === activeComponent)?.performanceCategoryList ?? []}
						fineTuningSettingsRef={fineTuningSettingsRef}
						performanceService={performanceService}
					/>}
			</div>

			<MoveZone carId={vehicleId} styles={{ height: 550, width: "50%", top: "60%" }} />

			<Toaster position='bottom-center' />
		</div>
	)
}